/**
 * Centralized Environment Detection Utility
 * 
 * This is the SINGLE SOURCE OF TRUTH for environment detection across the entire application.
 * All other files should import and use functions from this file.
 * 
 * Detection Priority (most reliable to least):
 * 1. Build-time environment variable (REACT_APP_FIREBASE_ENV) - Set during build, immutable
 * 2. Explicit hostname matching (exact known domains) - Safety net for custom domains
 * 3. Localhost detection - Development safety
 * 4. Default to production - Safe fallback
 * 
 * Why this approach:
 * - Build-time vars are most reliable (baked into bundle, can't be wrong)
 * - Hostname matching handles edge cases (custom domains, staging.unwontedartistry.com)
 * - Localhost always uses staging for safety (prevents accidental production access)
 * - Single source prevents inconsistencies across files
 */

/**
 * Known staging hostnames (exact matches for reliability)
 * Add new staging domains here as needed
 */
const STAGING_HOSTNAMES = [
  'unwontedartistry-stage.web.app',
  'unwontedartistry-stage.firebaseapp.com',
  'staging.unwontedartistry.com',
  // Add other staging domains here
];

/**
 * Known production hostnames. Custom domains + any from REACT_APP_PRODUCTION_HOSTNAMES (comma-separated).
 * Do not hardcode client/project-specific *.web.app or *.firebaseapp.com here.
 */
const PRODUCTION_HOSTNAMES = [
  'unwontedartistry.com',
  'www.unwontedartistry.com',
  'campaign.unwontedartistry.com',
  ...(typeof process.env.REACT_APP_PRODUCTION_HOSTNAMES === 'string'
    ? process.env.REACT_APP_PRODUCTION_HOSTNAMES.split(',').map((h) => h.trim()).filter(Boolean)
    : []),
];

/**
 * Get the current environment
 * @returns 'staging' | 'production'
 */
export function getEnvironment(): 'staging' | 'production' {
  // Priority 1: Explicit production override (for testing production locally)
  if (process.env.REACT_APP_FORCE_PRODUCTION === 'true') {
    return 'production';
  }

  // Priority 2: Build-time environment variable (most reliable - set during build)
  // This is set correctly in build scripts:
  // - build:staging sets REACT_APP_FIREBASE_ENV=staging
  // - build:production sets REACT_APP_FIREBASE_ENV=production
  const buildTimeEnv = process.env.REACT_APP_FIREBASE_ENV;
  if (buildTimeEnv === 'staging' || buildTimeEnv === 'production') {
    return buildTimeEnv;
  }

  // Priority 3: Hostname detection (safety net for custom domains or edge cases)
  // Only check if we're in a browser environment
  if (typeof window !== 'undefined' && window.location && window.location.hostname) {
    const hostname = window.location.hostname.toLowerCase();
    
    // Exact match for known staging domains
    if (STAGING_HOSTNAMES.some(stagingHost => hostname === stagingHost)) {
      return 'staging';
    }
    
    // Exact match for known production domains
    if (PRODUCTION_HOSTNAMES.some(prodHost => hostname === prodHost)) {
      return 'production';
    }
  }

  // Priority 4: Localhost detection (always staging for safety)
  if (typeof window !== 'undefined' && window.location && window.location.hostname) {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'staging';
    }
  }

  // Priority 5: Default to production (safest fallback)
  return 'production';
}

/**
 * Returns true if the current hostname is a known production hostname.
 * Used to fail fast: never use staging bootstrap on production domains.
 */
export function isProductionHostname(): boolean {
  if (typeof window === 'undefined' || !window.location?.hostname) return false;
  const hostname = window.location.hostname.toLowerCase();
  return PRODUCTION_HOSTNAMES.some((h) => h.toLowerCase() === hostname);
}

/**
 * Check if current environment is staging
 */
export function isStaging(): boolean {
  return getEnvironment() === 'staging';
}

/**
 * Check if current environment is production
 */
export function isProduction(): boolean {
  return getEnvironment() === 'production';
}

/**
 * True when the site is loaded from a typical dev-machine host (not Firebase Hosting).
 * CRA/Vite do not serve `/api/*` unless proxied; checkout must call a full `adminApi` URL instead of a relative `/api/shop/...`.
 */
export function isLikelyLocalDevelopmentHostname(): boolean {
  if (typeof window === 'undefined' || !window.location?.hostname) return false;
  const h = window.location.hostname.toLowerCase();
  if (h === 'localhost' || h === '127.0.0.1' || h === '::1' || h === '[::1]') return true;
  if (h.endsWith('.local')) return true;
  if (h === '0.0.0.0') return true;
  if (/^192\.168\.\d{1,3}\.\d{1,3}$/.test(h)) return true;
  if (/^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(h)) return true;
  const m = h.match(/^172\.(\d{1,3})\.\d{1,3}\.\d{1,3}$/);
  if (m) {
    const second = parseInt(m[1], 10);
    if (second >= 16 && second <= 31) return true;
  }
  return false;
}

/**
 * Check if running in development mode (NODE_ENV)
 */
export function isDevelopment(): boolean {
  return process.env.NODE_ENV === 'development';
}

/**
 * Get debug information about environment detection
 * Useful for troubleshooting environment issues
 */
export function getEnvironmentDebugInfo(): {
  environment: 'staging' | 'production';
  buildTimeEnv: string | undefined;
  hostname: string | undefined;
  isLocalhost: boolean;
  detectionMethod: string;
} {
  const buildTimeEnv = process.env.REACT_APP_FIREBASE_ENV;
  const hostname = typeof window !== 'undefined' && window.location ? window.location.hostname : undefined;
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  
  let detectionMethod = 'unknown';
  if (process.env.REACT_APP_FORCE_PRODUCTION === 'true') {
    detectionMethod = 'force_production_override';
  } else if (buildTimeEnv === 'staging' || buildTimeEnv === 'production') {
    detectionMethod = 'build_time_env_var';
  } else if (hostname && STAGING_HOSTNAMES.includes(hostname.toLowerCase())) {
    detectionMethod = 'staging_hostname_match';
  } else if (hostname && PRODUCTION_HOSTNAMES.includes(hostname.toLowerCase())) {
    detectionMethod = 'production_hostname_match';
  } else if (isLocalhost) {
    detectionMethod = 'localhost_detection';
  } else if (isLikelyLocalDevelopmentHostname()) {
    detectionMethod = 'local_dev_hostname';
  } else {
    detectionMethod = 'default_production';
  }

  return {
    environment: getEnvironment(),
    buildTimeEnv,
    hostname,
    isLocalhost,
    detectionMethod,
  };
}
